import { createReactive } from "@supergrain/kernel"

// Aliased so the sets below aren't written as `Set<ReadonlySet<string>>`-style nested
// generics: the `>>` trips the Tailwind language server's lexer (it reads it as an
// unclosed JSX tag), which kills className autocomplete in files that import this. See
// tailwindcss-intellisense#868.
type ExpandedSectionIds = ReadonlySet<string>

/**
 * In the accordion layout, whether opening a section closes the previously open one
 * (a strict accordion) instead of letting any number of sections be open at once.
 */
const ONLY_ONE_SECTION_OPEN = false

/** Which of a menu's accordion sections are expanded. */
export interface ExpandedSectionsStore {
  readonly expandedIds: ExpandedSectionIds
  setExpanded: (id: string, isExpanded: boolean) => void
  /** Back to the defaults. Called between tests. */
  reset: () => void
}

/**
 * One store per kind of menu, shared by every instance of it: expanding "Templates" on
 * one lesson expands it on all of them. A module-level reactive object rather than React
 * context because each slot is its own react-render root.
 */
export function createExpandedSections(defaultExpandedIds: string[]): ExpandedSectionsStore {
  const store = createReactive({
    expandedIds: new Set(defaultExpandedIds) as ExpandedSectionIds,
  })

  return {
    get expandedIds() {
      return store.expandedIds
    },

    setExpanded(id: string, isExpanded: boolean) {
      const current = store.expandedIds
      if (current.has(id) === isExpanded) return
      if (ONLY_ONE_SECTION_OPEN) {
        store.expandedIds = new Set(isExpanded ? [id] : [])
      } else {
        const next = new Set(current)
        if (isExpanded) next.add(id)
        else next.delete(id)
        store.expandedIds = next
      }
    },

    reset() {
      store.expandedIds = new Set(defaultExpandedIds)
    },
  }
}
